import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { getAuthHeaders } from '../utils/authHelper';
import './UserManagePage.css';

const UserManagePage = () => {
  const { user: currentUser } = useAuth();
  const { getServer, getSmartApiUrl } = useContext(ServerConfigContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');

  const roleOptions = [
    { value: 'user', label: '👤 일반 사용자' },
    { value: 'moderator', label: '🛡️ 모더레이터' },
    { value: 'admin', label: '👑 관리자' }
  ];

  const gradeOptions = [
    { value: 'newbie', label: '뉴비' },
    { value: 'bronze', label: '브론즈' },
    { value: 'silver', label: '실버' },
    { value: 'gold', label: '골드' },
    { value: 'platinum', label: '플래티넘' },
    { value: 'diamond', label: '다이아몬드' }
  ];

  // API URL 생성 함수 - 엔드포인트에 따라 자동으로 서버 선택
  const getApiUrl = (endpoint) => {
    if (getSmartApiUrl) {
      return getSmartApiUrl(endpoint);
    }
    // 폴백: 기존 방식
    return `/board-api/api/v1${endpoint}`;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/auth/users'), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        let errorMessage = `사용자 목록 조회 실패: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success && result.data && result.data.users) {
        setUsers(result.data.users);
      } else {
        setUsers([]);
      }
      setError('');
    } catch (err) {
      console.error('사용자 목록 조회 오류:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await fetch(getApiUrl(`/auth/users/${userId}`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        let errorMessage = `사용자 정보 수정 실패: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }

      await fetchUsers();
      setEditingUser(null);
      setError('');
      console.log(`✅ 사용자 정보 수정 성공: ${userId}`);
    } catch (err) {
      console.error('사용자 정보 수정 오류:', err);
      setError(err.message);
    }
  };

  const handleRoleChange = async (user, newRole) => {
    // 자신의 역할은 변경할 수 없음
    if (user.user_id === currentUser?.user_id) {
      setError('자신의 역할은 변경할 수 없습니다.');
      return;
    }

    // 관리자만 모더레이터나 관리자로 승격/강등 가능
    if (currentUser?.role !== 'admin' && (newRole === 'admin' || newRole === 'moderator')) {
      setError('관리자만 모더레이터나 관리자 역할을 부여할 수 있습니다.');
      return;
    }

    const roleLabel = roleOptions.find(opt => opt.value === newRole)?.label;
    if (!window.confirm(`"${user.nickname || user.user_id}"의 역할을 "${roleLabel}"로 변경하시겠습니까?`)) {
      return;
    }

    await handleUpdateUser(user.user_id, { role: newRole });
  };

  const handleGradeChange = async (user, newGrade) => {
    const gradeLabel = gradeOptions.find(opt => opt.value === newGrade)?.label;
    if (!window.confirm(`"${user.nickname || user.user_id}"의 등급을 "${gradeLabel}"로 변경하시겠습니까?`)) {
      return;
    }

    await handleUpdateUser(user.user_id, { grade: newGrade });
  };

  const handleActiveToggle = async (user) => {
    const action = user.is_active ? '비활성화' : '활성화';
    if (!window.confirm(`"${user.nickname || user.user_id}"를 ${action}하시겠습니까?`)) {
      return;
    }

    await handleUpdateUser(user.user_id, { is_active: !user.is_active });
  };

  // 사용자 삭제
  const handleDeleteUser = async (user) => {
    // 자신을 삭제할 수 없음
    if (user.user_id === currentUser?.user_id) {
      setError('자신의 계정은 삭제할 수 없습니다.');
      return;
    }

    // 관리자만 삭제 가능
    if (currentUser?.role !== 'admin') {
      setError('관리자만 사용자를 삭제할 수 있습니다.');
      return;
    }

    const confirmMessage = `정말로 사용자 "${user.nickname || user.user_id}"를 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없습니다.\n⚠️ 사용자의 모든 게시글과 댓글도 함께 삭제될 수 있습니다.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/auth/users/${user.user_id}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        let errorMessage = `사용자 삭제 실패: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.success) {
        console.log(`✅ 사용자 삭제 성공: ${user.user_id}`);
        await fetchUsers(); // 목록 새로고침
        setError('');
        alert(`사용자 "${user.nickname || user.user_id}"가 성공적으로 삭제되었습니다.`);
      } else {
        throw new Error(result.message || '사용자 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('사용자 삭제 오류:', err);
      setError(err.message);
    }
  };

  const getRoleLabel = (role) => {
    const option = roleOptions.find(opt => opt.value === role);
    return option ? option.label : role;
  };

  const getGradeLabel = (grade) => {
    const option = gradeOptions.find(opt => opt.value === grade);
    return option ? option.label : grade;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.nickname && user.nickname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesGrade = filterGrade === 'all' || user.grade === filterGrade;
    
    return matchesSearch && matchesRole && matchesGrade;
  });

  if (loading) {
    return (
      <div className="user-management-page">
        <div className="loading-message">👥 사용자 목록을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="page-header">
        <h2>👥 유저 관리</h2>
        <div className="user-stats">
          <span className="stat-item">총 {users.length}명</span>
          <span className="stat-item">활성 {users.filter(u => u.is_active).length}명</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="사용자 ID, 닉네임, 이메일로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-selects">
          <select 
            value={filterRole} 
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="all">모든 역할</option>
            {roleOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <select 
            value={filterGrade} 
            onChange={(e) => setFilterGrade(e.target.value)}
          >
            <option value="all">모든 등급</option>
            {gradeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 사용자 목록 테이블 */}
      <div className="users-table-container">
        {filteredUsers.length === 0 ? (
          <div className="empty-message">
            {searchTerm || filterRole !== 'all' || filterGrade !== 'all' 
              ? '🔍 검색 조건에 맞는 사용자가 없습니다.' 
              : '👥 등록된 사용자가 없습니다.'}
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>사용자 ID</th>
                <th>닉네임</th>
                <th>이메일</th>
                <th>역할</th>
                <th>등급</th>
                <th>상태</th>
                <th>가입일</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.user_id} className={!user.is_active ? 'inactive-user' : ''}>
                  <td className="user-id">
                    {user.user_id}
                    {user.user_id === currentUser?.user_id && (
                      <span className="current-user-badge">본인</span>
                    )}
                  </td>
                  <td className="nickname">{user.nickname || '-'}</td>
                  <td className="email">{user.email || '-'}</td>
                  <td className="role-cell">
                    <span className={`role-badge role-${user.role}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="grade-cell">
                    <span className={`grade-badge grade-${user.grade}`}>
                      {getGradeLabel(user.grade)}
                    </span>
                  </td>
                  <td className="status-cell">
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? '✅ 활성' : '❌ 비활성'}
                    </span>
                  </td>
                  <td className="created-date">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('ko-KR') : '-'}
                  </td>
                  <td className="actions">
                    <div className="action-buttons">
                      {/* 역할 변경 */}
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                        disabled={user.user_id === currentUser?.user_id}
                        className="role-select"
                      >
                        {roleOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      {/* 등급 변경 */}
                      <select
                        value={user.grade}
                        onChange={(e) => handleGradeChange(user, e.target.value)}
                        className="grade-select"
                      >
                        {gradeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      {/* 활성화/비활성화 토글 */}
                      <button
                        className={`toggle-button ${user.is_active ? 'deactivate' : 'activate'}`}
                        onClick={() => handleActiveToggle(user)}
                        disabled={user.user_id === currentUser?.user_id}
                      >
                        {user.is_active ? '🚫 비활성' : '✅ 활성'}
                      </button>
                      
                      {/* 사용자 삭제 버튼 (관리자만, 자신은 제외) */}
                      {currentUser?.role === 'admin' && user.user_id !== currentUser?.user_id && (
                        <button
                          className="delete-user-button"
                          onClick={() => handleDeleteUser(user)}
                          title="사용자 삭제 (관리자 전용)"
                        >
                          🗑️ 삭제
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 도움말 */}
      <div className="help-section">
        <h3>💡 사용법</h3>
        <ul>
          <li><strong>역할 변경</strong>: 드롭다운에서 새로운 역할을 선택하면 즉시 변경됩니다.</li>
          <li><strong>등급 변경</strong>: 사용자의 활동에 따라 등급을 조정할 수 있습니다.</li>
          <li><strong>계정 상태</strong>: 비활성화된 사용자는 로그인할 수 없습니다.</li>
          <li><strong>사용자 삭제</strong>: 🗑️ 관리자만 다른 사용자를 삭제할 수 있습니다. (자신은 삭제 불가)</li>
          <li><strong>권한 제한</strong>: 자신의 정보는 변경할 수 없으며, 관리자만 모든 역할을 부여할 수 있습니다.</li>
        </ul>
      </div>
    </div>
  );
};

export default UserManagePage;